import React, { useState, useEffect, useCallback } from 'react';
import { useAdminSocket } from '../hooks/useAdminSocket';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminPageHeader } from '../components/layout/AdminPageHeader';
import { Card, Badge, Button, Spinner } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { adminApi } from '../services/api';
import { toast } from 'sonner';

const AdminQueue = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.getRecentOrders();
      setOrders(data || []);
    } catch (err) {
      toast.error(t('adminQueue.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useAdminSocket({ onQueueUpdate: fetchQueue });

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const filteredOrders = orders.filter(order => 
    (order.user?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (order.id || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedOrders = filteredOrders.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);

  if (loading) return <div className="h-[60vh] flex items-center justify-center"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10 pb-10">
      <AdminPageHeader
        tag="LIVE QUEUE"
        title={t('dashboard.queueManagement') || 'Queue Management'}
        subtitle={`${orders.length} ${t('dashboard.totalInQueue') || 'RECENT ORDERS IN SYSTEM'}`}
        icon="hourglass_empty"
      >
        <div className="relative group flex-1">
          <input
            type="text"
            placeholder={t('dashboard.searchQueue') || 'Search orders...'}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-80 h-14 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-3xl px-12 outline-none focus:border-[#1e9f6b]/50 transition-all font-bold text-sm dark:text-white shadow-xl shadow-slate-900/5"
          />
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#1e9f6b] transition-colors">
            search
          </span>
        </div>
        <Button
          variant="ghost"
          onClick={fetchQueue}
          className="h-14 w-14 rounded-3xl p-0 hover:bg-[#1e9f6b]/10 hover:text-[#1e9f6b] transition-colors"
        >
          <span className="material-symbols-outlined">refresh</span>
        </Button>
      </AdminPageHeader>

      {/* Queue Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="overflow-hidden bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-2xl shadow-slate-900/5">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-900/30">
                  <th className="px-10 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.orderID')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.user')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.cylinders')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.totalPrice')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.status')}</th>
                  <th className="px-10 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em] text-right">{t('dashboard.date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                <AnimatePresence mode="popLayout">
                  {paginatedOrders.map((order, idx) => (
                    <motion.tr 
                      key={order.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ delay: idx * 0.05 }}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all duration-300 group"
                    >
                      <td className="px-10 py-6">
                        <div className="flex items-center gap-4">
                           <div className="h-10 w-10 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center font-black text-[10px] text-[#1e9f6b] shadow-inner group-hover:scale-110 transition-all uppercase">
                              #{order.id.slice(0, 4)}
                           </div>
                        </div>
                      </td>

                      <td className="px-6 py-6">
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center overflow-hidden transition-all duration-500 italic uppercase shadow-inner">
                            {order.user?.avatarUrl ? (
                              <img src={order.user.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <span className="font-black text-xs text-slate-400">{order.user?.name?.[0] || 'U'}</span>
                            )}
                          </div>
                          <div>
                            <div className="font-black text-xs italic uppercase tracking-tight text-slate-900 dark:text-white">{order.user?.name || t('operatorDashboard.customer')}</div>
                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{order.user?.phone}</div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-6">
                        <span className="text-xs font-black italic uppercase tracking-tighter text-slate-700 dark:text-slate-300">{order.quantity} {t('common.pcs') || "ta"}</span>
                      </td>

                      <td className="px-6 py-6">
                        <span className="text-xs font-black italic uppercase tracking-tighter text-[#1e9f6b]">{(order.totalPrice || 0).toLocaleString()} {t('common.uzs')}</span>
                      </td>

                      <td className="px-6 py-6">
                        <Badge 
                          variant={
                            order.status === 'COMPLETED' ? 'green' : 
                            order.status === 'PENDING' ? 'amber' : 
                            order.status === 'PROCESSING' ? 'blue' : 
                            'red'
                          }
                          pulse={order.status === 'PENDING'}
                          className="rounded-xl font-black italic uppercase text-[8px] tracking-wider px-3 py-1.5 border-0"
                        >
                          {order.status}
                        </Badge>
                      </td>

                      <td className="px-10 py-6 text-right">
                         <div className="text-[10px] font-black text-slate-400 uppercase italic tracking-widest">
                            {new Date(order.createdAt).toLocaleDateString()}
                         </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-10 py-8 border-t border-slate-50 dark:border-slate-800/50 flex justify-between items-center bg-slate-50/10 dark:bg-slate-800/5">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">
                {t('common.pageOf', { page, total: totalPages })}
              </div>
              <div className="flex gap-3">
                <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-2xl h-12 px-6 font-black uppercase italic text-[10px] tracking-widest bg-white dark:bg-slate-800 shadow-sm">
                  <span className="material-symbols-outlined text-sm mr-2">arrow_back</span>
                  {t('common.prev')}
                </Button>
                <Button size="sm" variant="ghost" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="rounded-2xl h-12 px-6 font-black uppercase italic text-[10px] tracking-widest bg-white dark:bg-slate-800 shadow-sm">
                  {t('common.next')}
                  <span className="material-symbols-outlined text-sm ml-2">arrow_forward</span>
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminQueue;
